import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Box3, Group, Vector3 } from 'three';

import { Html, PerspectiveCamera, Stage, View } from '@react-three/drei';
import { styled } from '@spazious/dollhouse-theme';
import { Dimensions } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { useModelLoader } from '../../hooks/index.js';
import { ModelPreviewAsset } from '../ModelPreviewAsset/index.js';

const cacheDimensions = new Map<string, Vector3>();
const cacheNavMeshStatus = new Map<string, boolean>();

type DimensionInfoComponent = React.ComponentType<
  React.HTMLAttributes<HTMLDivElement> & {
    dimensions: Dimensions;
  }
>;

interface InfoToggleButtonComponentProps {
  toggle: () => void;
}
type InfoToggleButtonComponent = React.ComponentType<InfoToggleButtonComponentProps>;

export interface ModelPreviewProps {
  url: string;
  name: string;
  thumbnail?: string | null;
  type?: 'canvas' | 'image';
  fallback?: React.ReactNode;
  labelFormatter?: (value: number | string) => number | string;
  dimensionInfoComponent?: DimensionInfoComponent;
  infoToggleButtonComponent?: InfoToggleButtonComponent;
  showNavMeshIndicator?: boolean;
  showInfoTrigger?: 'hover' | 'button';
}

const Card = styled('div')`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  align-items: center;
  justify-content: center;
  border-radius: var(--shape-rounded-radius);
  border: solid 1px var(--spz-sys-color-surface-container-highest);
  background-color: var(--spz-sys-color-surface-variant);
  transition: border, background-color 0.2s ease-in-out;
  cursor: grab;
  display: flex;
  flex-direction: column;
  position: relative;
  &:hover {
    border: solid 1px var(--spz-sys-color-inverse-surface);
    background-color: var(--spz-sys-color-surface-container-lowest);
  }
`;

const ModelPreviewWrapper = styled('div').withConfig({ displayName: 'ModelPreviewWrapper' })`
  width: 100%;
  max-width: 150px;
  max-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const NavMeshIndicator = styled('div')`
  width: 4px;
  height: 4px;
  border-radius: 2px;
  background-color: green;
  position: absolute;
  top: 8px;
  left: 8px;
`;

const ImageWrapper = styled('div').withConfig({ displayName: 'Image' })`
  position: relative;
  border-radius: var(--shape-rounded-radius);
  overflow: hidden;
  min-height: 100px;
  width: 100%;
`;

export const ModelPreview: React.FC<ModelPreviewProps> = ({
  url,
  name,
  thumbnail,
  type = 'image',
  fallback,
  labelFormatter = (arg) => arg,
  dimensionInfoComponent: DimensionInfo,
  infoToggleButtonComponent: InfoToggleButton,
  showNavMeshIndicator = false,
  showInfoTrigger = 'button',
}) => {
  const [hovered, setHover] = useState(false);
  const [infoVisible, setInfoVisible] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const toggleInfo = useEventCallback(() => {
    setInfoVisible(!infoVisible);
  });

  const { scene } = useModelLoader(url);

  const handlePointerEnter = useEventCallback(() => {
    setHover(true);
    if (showInfoTrigger === 'hover' && (imageRef.current || cacheDimensions.has(url))) {
      // show info only AFTER image is loaded
      setInfoVisible(true);
    }
  });

  const handlePointerLeave = useEventCallback(() => {
    setHover(false);
    if (showInfoTrigger === 'hover') {
      setInfoVisible(false);
    }
  });

  const updateDimensions = useEventCallback((node: Group | null) => {
    if (!node || cacheDimensions.has(url)) {
      return;
    }
    const dimensions = new Box3().setFromObject(node).getSize(new Vector3());
    cacheDimensions.set(url, dimensions);
    cacheNavMeshStatus.set(url, !!node.navMesh);
  });

  useEffect(() => {
    updateDimensions(scene);
  }, [scene, updateDimensions]);

  const centerProps = useMemo(
    () => ({
      cacheKey: url,
      precise: true,
    }),
    [url]
  );

  const dimensions = cacheDimensions.get(url) ?? new Vector3();
  const hasNavMesh = cacheNavMeshStatus.get(url) ?? false;

  const modelDimensions = useMemo<Dimensions>(
    () => ({
      length: labelFormatter(dimensions.x),
      height: labelFormatter(dimensions.y),
      width: labelFormatter(dimensions.z),
    }),
    [labelFormatter, dimensions.x, dimensions.y, dimensions.z]
  );

  if (type === 'image') {
    return (
      <Card style={{ backgroundColor: 'transparent', minHeight: thumbnail ? 'auto' : '100%' }}>
        <ImageWrapper onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
          {thumbnail ? (
            <img
              ref={imageRef}
              alt={name}
              src={thumbnail!}
              style={{
                objectFit: 'contain',
                width: '100%',
                height: infoVisible ? 'auto' : '100%',
                WebkitTouchCallout: 'none', // avoid displaying share panel when touch and hold on image
              }}
            />
          ) : (
            <div style={{ margin: 'var(--spacing-gap)' }}>{name}</div>
          )}
          {DimensionInfo && (
            <>
              <DimensionInfo
                dimensions={modelDimensions}
                style={{
                  visibility: infoVisible ? 'visible' : 'hidden',
                  bottom: 'var(--spacing-gap)',
                  left: '0px',
                  right: '0px',
                }}
              />
            </>
          )}
          {showNavMeshIndicator && hasNavMesh && <NavMeshIndicator />}
        </ImageWrapper>
        {showInfoTrigger === 'button' && InfoToggleButton && (
          <InfoToggleButton toggle={toggleInfo} />
        )}
      </Card>
    );
  }

  return (
    <Card style={{ backgroundColor: 'transparent' }}>
      <ModelPreviewWrapper onPointerEnter={handlePointerEnter} onPointerLeave={handlePointerLeave}>
        <Suspense fallback={fallback ? <Html center>{fallback}</Html> : null}>
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: infoVisible ? 'calc(100% - 40px)' : '100%',
            }}
          >
            <Stage
              adjustCamera={1.5}
              center={centerProps}
              environment="warehouse"
              intensity={0.5}
              preset="portrait"
              shadows="contact"
            >
              <ModelPreviewAsset ref={updateDimensions} rotate={hovered} url={url} />
            </Stage>
            <PerspectiveCamera makeDefault fov={35} position={[1.5, 1.5, -1.5]} />
          </View>
        </Suspense>
        {DimensionInfo && (
          <>
            <DimensionInfo
              dimensions={modelDimensions}
              style={{
                visibility: infoVisible ? 'visible' : 'hidden',
                position: 'absolute',
                bottom: '0px',
                width: '100%',
              }}
            />
          </>
        )}
        {showNavMeshIndicator && hasNavMesh && <NavMeshIndicator />}
      </ModelPreviewWrapper>
      {showInfoTrigger === 'button' && InfoToggleButton && <InfoToggleButton toggle={toggleInfo} />}
    </Card>
  );
};
